import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
} from '@reduxjs/toolkit';
import { AppState } from '.';
import {
  ActionStatus,
  exceptionOf, GetSurveyRequest,
  SerializedException,
  SurveyConfig,
  SurveyName,
} from '../models';
import { SurveyService } from '../services';

export const SURVEY_FEATURE_KEY = 'survey';

const surveyAdapter = createEntityAdapter<SurveyConfig>();

interface SurveyState extends EntityState<SurveyConfig> {
  status?: ActionStatus;
  errors: SerializedException[];
}

const createInitialState = (): SurveyState => surveyAdapter.getInitialState({
  errors: [],
});

export const doGetSurvey = createAsyncThunk(
  'survey/getSurvey',
  async (getSurveyRequest: GetSurveyRequest, { rejectWithValue }) => {
    try {
      const surveyConfig = await SurveyService.getTurnOffAutoRenewSurveyUrl(getSurveyRequest);
      return { surveyConfig: { ...surveyConfig, id: getSurveyRequest.context } };
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

const surveySlice = createSlice({
  name: SURVEY_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(doGetSurvey.pending, state => {
      state.status = ActionStatus.LOADING;
    });
    builder.addCase(doGetSurvey.fulfilled, (state, action) => {
      state.status = ActionStatus.SUCCESS;
      surveyAdapter.setOne(state, action.payload.surveyConfig);
    });
    builder.addCase(doGetSurvey.rejected, (state, action) => {
      state.status = ActionStatus.ERROR;
      state.errors.push(action.error as SerializedException);
    });
  },
});

const selectSurveyFeature = (state: AppState) => state[SURVEY_FEATURE_KEY];
const { selectAll } = surveyAdapter.getSelectors(selectSurveyFeature);

export const selectSurvey = (surveyName: SurveyName) => createSelector(
  selectAll,
  surveys => surveys.find(survey => survey.id === surveyName),
);

export default surveySlice.reducer;
